import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import type { LadderAnswer } from '../Ladder/claims';
import type { AgentRow } from '../Agents/api';

const TRACKABLE = ['why', 'data'];

/** What an answer asked inside an objective says about it (#766), from the objective as the viewer can open it NOW. */
export function objectiveText(agent: AgentRow): string {
  const o = agent.objective;
  const name = o.label || o.metric.split('.').pop() || o.metric;
  // the objective's numbers only where its page shows them (the metric is in the viewer's catalog)
  const target = agent.metric_visible === false || !o.target ? null
    : o.target.kind === 'pct' ? `${o.target.value > 0 ? '+' : ''}${o.target.value}%` : String(o.target.value);
  const guardrails = agent.guardrails.length;
  return [`Asked inside ${name}`, o.metric, target ? `target ${target}${o.deadline ? ` by ${o.deadline}` : ''}` : null,
    guardrails ? `${guardrails} guardrail${guardrails === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
}

/**
 * The objective line under an answer (#766, Stroma): an answer asked inside an objective says so, and a Why or Data
 * answer about a metric offers "Track this as an objective" — New objective (#573) drafted from that metric.
 */
/** "Track this as an objective" for an answer, or null when it offers none (the line's button, and the More menu's). */
export function useTrackObjective(turn: LandingAgentTurn): (() => void) | null {
  const ladder = turn.result?.ladder as LadderAnswer | null | undefined;
  const oid = ladder?.objective?.id || null;
  const agent = useVoyagerStore((s) => (oid ? s.agents.find((a) => a.id === oid) || null : null));
  const threadId = useVoyagerStore((s) => s.currentThreadId);
  if (!ladder) return null;
  const metric = ladder.thread_context?.primary_metric || null;
  const trackable = turn.status === 'done' && !!metric && TRACKABLE.includes(ladder.route?.route || '')
    && (!agent || agent.objective.metric !== metric);
  if (!trackable) return null;
  return () => useVoyagerStore.getState().navigateToNewAgent(metric as string, ladder.thread_context?.perspective || undefined, threadId);
}

/** `track={false}`: the answer's More menu offers Track this, so the line keeps only what it says. */
export default function ObjectiveLine({ turn, track = true }: { turn: LandingAgentTurn; track?: boolean }) {
  const ladder = turn.result?.ladder as LadderAnswer | null | undefined;
  const oid = ladder?.objective?.id || null;
  const agent = useVoyagerStore((s) => (oid ? s.agents.find((a) => a.id === oid) || null : null));
  const trackObjective = useTrackObjective(turn);
  if (!ladder) return null;
  const trackable = track && !!trackObjective;
  if (!oid && !trackable) return null;
  return (
    <Box data-testid="objective-line" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      {oid && <Typography variant="caption" data-testid="objective-said" sx={{ color: 'var(--accent-ink)', fontWeight: 600, overflowWrap: 'anywhere' }}>
        {agent ? objectiveText(agent) : 'Asked inside an objective you can no longer open'}
      </Typography>}
      {trackable && <Button size="small" variant="text" data-testid="track-objective" sx={{ minWidth: 0, px: 0.75, textTransform: 'none', color: 'var(--accent)' }}
        onClick={trackObjective!}>
        Track this as an objective
      </Button>}
    </Box>
  );
}
